import { IChannelTransport, IChannelTransportFactory } from "./types";
import { ISocketLogger } from "./webSocket";

const NOOP = () => {};

export interface ICometChannel {
    getChannelId: () => string;
    getSubscriptions: () => string[];
    subscribe: (topicId: string, callback: (data: any) => void) => boolean;
    unsubscribe: (topicId: string) => Promise<void>;
    unsubscribeMultiple: (topicIds: string[]) => Promise<void>;
    notify: (
        topicId: string,
        obj: { senderId: string; [key: string]: any }
    ) => void;
    shutdown: () => Promise<void>;
    suspend: () => Promise<void>;
    resume: () => void;
    setConnection: (conn: ICallableConnection) => void;
    send: (message: string) => void;
}

interface ISubscription {
    id?: string;
    callback: (data: any) => void;
}

interface IWebSocketMessage {
    messages?: {
        subscriptionId: string;
        data: {
            senderId?: string;
            [key: string]: any;
        };
    }[];
}

interface IWebSocketMessageResult {
    result?: IWebSocketMessage;
}

interface ICallableConnection {
    call: (
        method: string,
        params: object
    ) => {
        beginInvoke: (
            onSuccess: (result: string[]) => void,
            onError: (error: Error) => void
        ) => void;
    };
}

const isMessagesResult = (
    results: IWebSocketMessage | IWebSocketMessageResult | undefined
): results is IWebSocketMessageResult =>
    !!(results as IWebSocketMessageResult | undefined)?.result;

export function CometChannel(
    database: string,
    channelId: string,
    transportFactory: IChannelTransportFactory,
    connectionCtrl: ICallableConnection,
    errorHandler: (e?: Error) => void,
    logger?: ISocketLogger,
    suspendedBool = false,
    onSubscriptionsUpdated?: (data?: string[]) => void,
    onReconnect?: () => void,
    onDisconnected?: () => void
) {
    var suspended = suspendedBool,
        connection = connectionCtrl,
        senders: { [key: string]: boolean } = {},
        processMessages = function (
            results: IWebSocketMessage | IWebSocketMessageResult | undefined
        ) {
            let messages = isMessagesResult(results)
                ? results.result?.messages
                : results?.messages;

            if (messages) {
                try {
                    for (let i = 0; i < messages.length; i++) {
                        let message = messages[i];
                        for (let key in subscriptions) {
                            if (subscriptions.hasOwnProperty(key)) {
                                let subscription = subscriptions[key];
                                if (
                                    message.subscriptionId ===
                                        subscription.id &&
                                    subscription.callback
                                ) {
                                    if (
                                        message.data.senderId &&
                                        senders[message.data.senderId]
                                    ) {
                                        delete senders[message.data.senderId];
                                    } else {
                                        subscription.callback(message.data);
                                    }
                                }
                            }
                        }
                    }
                } finally {
                    /* continue regardless of error */
                }
            } else if ((results as any) !== true) {
                //channel is dead
                return false;
            }
            return true;
        },
        /**
         * An alternate error handler for console handling, rather than show the yellow bar
         */
        consoleErrorHandler = function (error: Error) {
            logger?.error(`ChannelError ${error && error.message}`);
        },
        subscriptionsRequestScheduleId: ReturnType<typeof setTimeout> | null =
            null,
        resetScheduleSubscriptionsRequest = function () {
            if (subscriptionsRequestScheduleId) {
                clearTimeout(subscriptionsRequestScheduleId);
                subscriptionsRequestScheduleId = null;
            }
        },
        scheduleSubscriptionsRequest = function (timeout: number) {
            resetScheduleSubscriptionsRequest();
            subscriptionsRequestScheduleId = setTimeout(
                requestSubscriptions,
                timeout
            );
        },
        failedLastRequest = false,
        onConnect = function () {
            onReconnect && onReconnect();
            postponedSubscribe(); //subscribe only on pending/deprecated subscriptions
            failedLastRequest = false;
        },
        subscriptionsReceived = function (
            messages: IWebSocketMessage | IWebSocketMessageResult | undefined
        ) {
            if (!processMessages(messages)) {
                logger?.log("subscriptions will be invalidated");
                updateQueue(true, () =>
                    updateSubscriptions(true).catch(
                        scheduleReestablishingConnection
                    )
                );
            }
        },
        subscriptionsRequestFailed = function (e?: Error) {
            let aborted = e && (e as any).isAborted;

            if (!aborted) {
                errorHandler(e);
            }
            failedLastRequest = !aborted;
        },
        onDisconnect = function () {
            deprecateSubscriptions();
            onDisconnected && onDisconnected();
            !suspended && scheduleReestablishingConnection();
        },
        scheduleReestablishingConnection = function () {
            if (Object.keys(subscriptions).length) {
                scheduleSubscriptionsRequest(failedLastRequest ? 5000 : 1000);
            }
        },
        channelConnection: IChannelTransport | null,
        /*
         * Our jsonp caller - the timeout must match and be a little longer than our server comet timeout.
         */
        requestSubscriptions = function () {
            resetScheduleSubscriptionsRequest();
            if (!(channelConnection && channelConnection.isConnected())) {
                channelConnection = transportFactory.getConnection();
                channelConnection?.listen(
                    database,
                    channelId,
                    onConnect,
                    subscriptionsReceived,
                    subscriptionsRequestFailed,
                    onDisconnect
                );
            }
        },
        postponedSubscribe = (function () {
            let subscribeTimer: ReturnType<typeof setTimeout> | null = null;
            return function (interval = 0) {
                subscribeTimer && clearTimeout(subscribeTimer);
                subscribeTimer = setTimeout(updateQueue, interval);
            };
        })(),
        deprecateSubscriptions = function () {
            logger?.log("flagging subscriptions as deprecated");
            Object.keys(subscriptions).forEach(function (key) {
                delete subscriptions[key].id;
            });
        },
        // wait for previous subscription request
        updateQueue = (() => {
            let subP: Promise<void> | null;

            return (force?: boolean, onError?: () => void) => {
                if (subP) {
                    // subscribe can be called during "Subscribe" request
                    // if we call subscribe two times second one shouldn't be forced
                    subP.then(() => updateSubscriptions(false)).catch(
                        onError ?? (() => {})
                    );
                } else {
                    subP = updateSubscriptions(force).then(
                        () => {
                            subP = null;
                        },
                        () => {
                            onError && onError();
                            subP = null;
                        }
                    );
                }
            };
        })(),
        updateSubscriptions = function (force = false) {
            let topics = Object.keys(subscriptions).filter(
                    (key) => force || !subscriptions[key].hasOwnProperty("id")
                ),
                subscribed = false;

            if (topics.length > 0) {
                return new Promise<void>((resolve, reject) => {
                    connection
                        .call("Subscribe", {
                            topicIds: topics,
                            channelId: channelId,
                        })
                        .beginInvoke(
                            function (ids: string[]) {
                                for (let i = 0; i < topics.length; i++) {
                                    let sub = subscriptions[topics[i]];

                                    // May have unsubscribed in the meantime!
                                    if (sub) {
                                        const notSubscribedOrUpdatingSubscription =
                                            !sub.id || sub.id < ids[i];
                                        if (
                                            notSubscribedOrUpdatingSubscription
                                        ) {
                                            sub.id = ids[i];
                                            subscribed = true;
                                        }
                                    }
                                }

                                if (!subscribed) {
                                    reject(new Error("Can't be subscribed"));
                                } else if (!suspended) {
                                    onSubscriptionsUpdated &&
                                        onSubscriptionsUpdated(ids);
                                    scheduleSubscriptionsRequest(1000);
                                }

                                resolve(undefined);
                            },
                            function (e: any) {
                                reject(e);
                                if (e && !e.isAborted) {
                                    if (e.isDBInitializingException) {
                                        postponedSubscribe(5000);
                                    }
                                    errorHandler(e);
                                }
                            }
                        );
                });
            }

            onSubscriptionsUpdated && onSubscriptionsUpdated();
            return Promise.resolve();
        },
        subscriptions: { [key: string]: ISubscription } = {},
        self: ICometChannel,
        start = function () {
            scheduleSubscriptionsRequest(5000);
        },
        isStarted = () => !!subscriptionsRequestScheduleId;

    return (self = {
        getChannelId: () => channelId,
        getSubscriptions: () => Object.keys(subscriptions),
        /**
         * Subscribes to a PubSub topic
         */
        subscribe: function (topicId: string, callback: (data: any) => void) {
            if (subscriptions[topicId]) {
                throw new Error("Already subscribed to " + topicId);
            }

            subscriptions[topicId] = { callback };

            if (!suspended) {
                !isStarted() && start();
                postponedSubscribe();
            }
            return !suspended;
        },
        /**
         * Unsubscribe from a PubSub topic
         */
        unsubscribe: function (topicId: string, ignoreErrors?: boolean) {
            let sub = subscriptions[topicId];
            if (!sub) {
                return Promise.reject(
                    new Error("Not subscribed to " + topicId)
                );
            }

            if (suspended) {
                return Promise.resolve();
            }

            if (!sub.id) {
                delete subscriptions[topicId];
                return Promise.resolve();
            }

            return new Promise<void>((resolve, reject) => {
                let fn = ignoreErrors ? consoleErrorHandler : errorHandler;

                connection
                    .call("Unsubscribe", { subscriptionIds: [sub.id] })
                    .beginInvoke(
                        () => {
                            delete subscriptions[topicId];
                            resolve();
                        },
                        (e: Error) => {
                            reject(e);
                            fn(e);
                        }
                    );
            });
        },
        /**
         * Unsubscribe from multiple PubSub topics in a single API call
         */
        unsubscribeMultiple: function (topicIds: string[], ignoreErrors?: boolean) {
            if (!topicIds || topicIds.length === 0) {
                return Promise.resolve();
            }

            if (suspended) {
                return Promise.resolve();
            }

            // Separate topics into those with IDs and those without
            const topicsWithIds: string[] = [];
            const subscriptionIds: string[] = [];
            const topicsWithoutIds: string[] = [];

            topicIds.forEach(topicId => {
                const sub = subscriptions[topicId];
                if (sub) {
                    if (sub.id) {
                        topicsWithIds.push(topicId);
                        subscriptionIds.push(sub.id);
                    } else {
                        topicsWithoutIds.push(topicId);
                    }
                }
            });

            // Delete topics without IDs immediately
            topicsWithoutIds.forEach(topicId => {
                delete subscriptions[topicId];
            });

            // If no topics have subscription IDs, we're done
            if (subscriptionIds.length === 0) {
                return Promise.resolve();
            }

            // Make a single API call to unsubscribe from all topics with IDs
            return new Promise<void>((resolve, reject) => {
                let fn = ignoreErrors ? consoleErrorHandler : errorHandler;

                connection
                    .call("Unsubscribe", { subscriptionIds: subscriptionIds })
                    .beginInvoke(
                        () => {
                            topicsWithIds.forEach(topicId => {
                                delete subscriptions[topicId];
                            });
                            resolve();
                        },
                        (e: Error) => {
                            reject(e);
                            fn(e);
                        }
                    );
            });
        },
        /**
         * Notifies a topic with new data
         * @param topicId The topic to notify
         * @param obj The data to send
         */
        notify: function (topicId, obj) {
            // Send data to that topic
            if (!suspended) {
                senders[obj.senderId] = true;
                connection
                    .call("Notify", { topicId: topicId, data: obj })
                    .beginInvoke(NOOP, errorHandler);
            }
        },
        shutdown: function () {
            resetScheduleSubscriptionsRequest();
            if (channelConnection && channelConnection.isConnected()) {
                channelConnection.disconnect();
            }
            channelConnection = null;

            // Wait for all topics unsubscribed - use batch unsubscribe
            const topics = self.getSubscriptions();
            if (topics.length > 0) {
                return self.unsubscribeMultiple(topics);
            }
            return Promise.resolve();
        },
        suspend: function () {
            const suspendPromise = self.shutdown();
            suspended = true;
            return suspendPromise;
        },
        resume: function () {
            suspended = false;
            scheduleReestablishingConnection();
        },
        setConnection: function (conn: ICallableConnection) {
            connection = conn;
        },
        send: function (message: string) {
            if (channelConnection && channelConnection.isConnected()) {
                channelConnection.send(message);
            } else {
                errorHandler(new Error("Cannot send message: channel connection is not available"));
            }
        },
    });
}